import { Avatar, DropdownMenu, Flex, Text } from "@radix-ui/themes";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { FaCheck } from "react-icons/fa";
import { useMemo, useState, type ReactNode } from "react";

import ArtistProfileIcon from "@/assets/icons/Artist user account.svg?react";
import AddIcon from "@/assets/icons/Add plus.svg?react";
import { menuTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import { useAudiolab } from "@/components/providers/AudiolabProvider/hooks";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import type { BaseCreatorDto } from "@/lib/lutely-api/gen/dtos";
import AddArtistModal from "@/components/audiolab/AddArtistModal";

export type ArtistAvatarProps = {
  url?: string | null;
  size: number;
  iconSize: number;
  className?: string;
};

export function ArtistAvatar({
  url,
  size,
  iconSize,
  className,
}: ArtistAvatarProps) {
  const fallbackIcon = (
    <ArtistProfileIcon
      width={iconSize}
      height={iconSize}
      className="fill-text-secondary"
    />
  );

  return (
    <div
      style={{ width: size, height: size }}
      className={cn(
        "rounded-full overflow-hidden shrink-0 bg-background-quarternary flex items-center justify-center",
        className,
      )}
    >
      {url ? (
        <Avatar
          src={url}
          fallback={fallbackIcon}
          size="1"
          radius="full"
          className="w-full h-full"
        />
      ) : (
        fallbackIcon
      )}
    </div>
  );
}

function ArtistBadge({
  children,
  "data-testid": testId,
}: {
  children: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <span
      data-testid={testId}
      className="text-[9px] font-bold uppercase tracking-[0.08em] px-[6px] py-[1px] rounded-full bg-background-quarternary text-text-secondary shrink-0"
    >
      {children}
    </span>
  );
}

export function MainArtistBadge() {
  return (
    <ArtistBadge data-testid="artist-selector-main-badge">Main</ArtistBadge>
  );
}

export type ArtistSwitcherMenuProps = {
  /** Element that opens the menu. Must be a single focusable element. */
  trigger: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  /**
   * Pass false when the trigger lives inside a modal Dialog. Two stacked
   * modal layers each locking body pointer-events fight over the same tap on
   * touch devices, which reads as the menu closing right after opening.
   */
  modal?: boolean;
};

/**
 * The switcher's menu items alone, for embedding inside another DropdownMenu
 * (e.g. as a submenu of the mobile profile menu). The host owns the
 * AddArtistModal — a dropdown unmounts its content on close, so the modal
 * cannot live in here.
 */
export function ArtistSwitcherMenuItems({
  onAddArtist,
  withHeader = false,
}: {
  onAddArtist: () => void;
  /**
   * Fixed account header at the top — for the standalone flyout. The profile
   * menu embeds these items under its own identity header, so it leaves this
   * off.
   */
  withHeader?: boolean;
}) {
  const { selectedCreator, allCreators, setSelectedCreator } = useAudiolab();
  const {
    data: user,
    defaultCreator,
    isLabelSubscriber,
    canSwitchArtists,
  } = useLoggedInUser();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  // Main (default) artist always first, the rest alphabetical
  const creatorOptions = useMemo(() => {
    if (!canSwitchArtists) return [];
    return [...allCreators].sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      if (!a.stageName && !b.stageName) return 0;
      if (!a.stageName) return 1;
      if (!b.stageName) return -1;
      return a.stageName.localeCompare(b.stageName);
    });
  }, [allCreators, canSwitchArtists]);

  const handleSelectCreator = (creator: BaseCreatorDto) => {
    setSelectedCreator(creator);

    // From the homepage or anywhere in the marketplace, switching artists
    // takes you into that artist's audiolab (the dashboard renders the
    // selected artist's content)
    if (
      matchRoute({ to: "/" }) ||
      matchRoute({ to: "/marketplace", fuzzy: true })
    ) {
      navigate({ to: "/audiolab" });
      return;
    }

    // If we're on one of our own artists' profile pages, follow the switch
    // so the page shows the newly selected artist instead of the old one.
    const audiolabMatch = matchRoute({ to: "/audiolab/creators/$CREATOR_ID" });
    const publicMatch = matchRoute({ to: "/creators/$CREATOR_ID" });
    const currentParam = (audiolabMatch || publicMatch || undefined)
      ?.CREATOR_ID;
    if (!currentParam) return;

    const viewingOwnArtist = allCreators.some(
      (c) => c.creatorId === currentParam || c.slug === currentParam,
    );
    if (!viewingOwnArtist) return;

    navigate({
      to: audiolabMatch
        ? "/audiolab/creators/$CREATOR_ID"
        : "/creators/$CREATOR_ID",
      params: { CREATOR_ID: creator.creatorId },
    });
  };

  if (!canSwitchArtists) {
    return null;
  }

  const accountName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    defaultCreator?.stageName ||
    "Your account";

  return (
    <>
      {withHeader && (
        <>
          <Flex align="center" className="gap-[8px] px-[8px] py-[6px]">
            <ArtistAvatar
              url={defaultCreator?.profilePictureUrl}
              size={28}
              iconSize={14}
            />
            <Flex direction="column" className="min-w-0">
              <Text className="text-[14px] font-bold truncate">
                {accountName}
              </Text>
              <Text className="text-[11px] text-text-secondary">
                {allCreators.length}{" "}
                {allCreators.length === 1 ? "artist" : "artists"}
              </Text>
            </Flex>
          </Flex>
          <DropdownMenu.Separator className={menuTokens.separator} />
        </>
      )}
      {/* Only the artist list scrolls; the sections around it stay fixed. */}
      <div className="min-h-0 flex-1 overflow-y-auto max-h-[min(420px,45vh)]">
        {creatorOptions.map((creator, index) => {
          const isSelected = creator.creatorId === selectedCreator?.creatorId;
          return (
            <div key={creator.creatorId}>
              {/* Separator between the main artist and the rest */}
              {index === 1 && creatorOptions[0].isDefault && (
                <DropdownMenu.Separator className={menuTokens.separator} />
              )}
              <DropdownMenu.Item
                data-testid={
                  isSelected
                    ? "artist-selector-current"
                    : "artist-selector-option"
                }
                className={cn(
                  "cursor-pointer rounded-[6px]",
                  isSelected && "bg-background-accent-quinary",
                )}
                onClick={() => handleSelectCreator(creator)}
              >
                <Flex align="center" gap="2" className="flex-1 min-w-0">
                  <ArtistAvatar
                    url={creator.profilePictureUrl}
                    size={24}
                    iconSize={12}
                  />
                  <Text
                    className={cn(
                      "text-[14px] truncate flex-1",
                      isSelected
                        ? "font-bold text-brand-primary"
                        : "font-semibold",
                    )}
                  >
                    {creator.stageName || "Unnamed Artist"}
                  </Text>
                  {creator.isDefault && <MainArtistBadge />}
                  {isSelected && (
                    <FaCheck
                      size={11}
                      className="text-brand-primary shrink-0"
                    />
                  )}
                </Flex>
              </DropdownMenu.Item>
            </div>
          );
        })}
      </div>

      {/* Adding artists stays a label-plan feature; downgraded multi-artist
          accounts only switch between the artists they already have. */}
      {isLabelSubscriber && (
        <>
          <DropdownMenu.Separator className={menuTokens.separator} />

          <DropdownMenu.Item
            data-testid="artist-selector-add-artist"
            className="cursor-pointer rounded-[6px]"
            onClick={onAddArtist}
          >
            <Flex align="center" gap="2">
              <Flex className="w-[24px] h-[24px] flex-none items-center justify-center rounded-full bg-background-accent-quinary text-brand-primary">
                <AddIcon width={14} height={14} className="fill-current" />
              </Flex>
              <Text className="font-bold text-[14px] text-brand-primary">
                Add New Artist
              </Text>
            </Flex>
          </DropdownMenu.Item>
        </>
      )}
    </>
  );
}

/**
 * The artist-switching dropdown for Pro (label) subscribers: all artists with
 * the main (default) artist pinned first, the selected one highlighted, and
 * an "Add New Artist" action. Renders nothing for non-Pro plans.
 */
export default function ArtistSwitcherMenu({
  trigger,
  side = "bottom",
  sideOffset = 8,
  align = "start",
  modal = true,
}: ArtistSwitcherMenuProps) {
  const { canSwitchArtists } = useLoggedInUser();
  const [isAddArtistOpen, setIsAddArtistOpen] = useState(false);

  if (!canSwitchArtists) {
    return null;
  }

  return (
    <>
      <DropdownMenu.Root modal={modal}>
        <DropdownMenu.Trigger>{trigger}</DropdownMenu.Trigger>

        <DropdownMenu.Content
          variant="soft"
          color="gray"
          // Bounded height with fixed header/footer: only the artist list
          // inside scrolls, so Add New Artist stays reachable on long rosters.
          className="rounded-[10px] min-w-[220px] max-h-[min(600px,70vh)] flex flex-col overflow-hidden"
          side={side}
          sideOffset={sideOffset}
          align={align}
          collisionPadding={8}
        >
          <ArtistSwitcherMenuItems
            withHeader
            onAddArtist={() => setIsAddArtistOpen(true)}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      <AddArtistModal
        open={isAddArtistOpen}
        onOpenChange={setIsAddArtistOpen}
      />
    </>
  );
}
